/**
 * Diese Klasse steuert die Kommunikation zwischen zwei Frames.
 * In diesem Fall wird hiermit die Kommunikation zwischen dem Parent-Frame und
 * dem Child-Frame ermöglicht.
 */
export class Channel {
	static EventInit            = 'Channel.SynAck';
	static EventRequest         = 'Channel.request';
	static EventResponseResolve = 'Channel.response.resolve';
	static EventResponseReject  = 'Channel.response.reject';

	/**
	 * Erstellt ein neuen Channel der Nachrichten weiterleiten kann.
	 *
	 * @param {Object} handler Ein Handler, welches die Requests bearbeitet.
	 */
	constructor(handler = {}) {
		this.requestId           = 0;
		this.port                = null;
		this.sendetRequests      = {};
		this.handler             = handler;

		// Caching
		this.queuedTransmissions = [];
		this.queuedRequests      = [];
	}

	/**
	 * Diese Funktion sendetm dem Channel ein neuen Request.
	 *
	 * @param {String} name Name vom Handler auf der Target-Seite.
	 * @param {Object} payload Parameter für den Handler.
	 * @return {Promise}
	 */
	send(name, payload, options = undefined) {
		let requestId = this.requestId++;

		if (this.port !== null) {
			this.port.postMessage({
				type: Channel.EventRequest,
				requestId,
				name,
				payload: payload
			}, options);
		} else {
			this.queuedTransmissions.push({
				requestId,
				name,
				payload,
				options
			});
		}

		return new Promise((resolve, reject) => {
			this.sendetRequests[requestId] = {
				resolve,
				reject
			};
		});
	}

	/**
	 * Diese Callback-Funktion wird auf dem Host-Window hinter einem Namen abgespeichert.
	 * Diese Callback-Funktion kann anschließend der Client-Window mit dem hinterlegten
	 * Namen aufrufen.
	 *
	 * @callback ChannelCallback
	 * @param {Object} params
	 * @param {PardotChannel} channel
	 */
	/**
	 * Diese Funktion erstellt eine neue Zielfunktion, die von den Targets aufgerufen
	 * werden können.
	 *
	 * @param {String} name Der Name der Zielfunktion.
	 * @param {ChannelCallback} callback Die Callback-Funktion die aufgerufen wird,
	 * sobald ein Target dies wünscht.
	 */
	on(name, callback) {
		this.handler[name] = callback;
		if (this.queuedRequests[name]) {
			Array.prototype.forEach.call(this.queuedRequests[name], (queuedRequest) => {
				this.onRequest(queuedRequest);
			});
			delete this.queuedRequests[name];
		}
	}

	/**
	 * Wenn diese Funktion aufgerufen wird, dann wurde eine
	 * Verbindung zum anderen System hergestellt.
	 *
	 * @param {MessagePort} port Der Port vom Target.
	 */
	start(port) {
		this.port = port;
		this.port.addEventListener('message', this.onMessage.bind(this), false);
		this.port.start();
		Array.prototype.forEach.call(this.queuedTransmissions, (queuedTransmission) => {
			this.port.postMessage({
				type: Channel.EventRequest,
				requestId: queuedTransmission.requestId,
				name: queuedTransmission.name,
				payload: queuedTransmission.payload
			}, queuedTransmission.options);
		});
		this.queuedTransmissions = false;
	}

	/**
	 * Diese Funktion wird ausgeführt, wenn eine Nachricht über diesen
	 * Channel abgefangen wird.
	 *
	 * @param {Event} event
	 */
	onMessage(event) {
		let type      = event?.data?.type ?? '';
		let requestId = event?.data?.requestId ?? 0;
		let name      = event?.data?.name ?? '';
		let payload   = event?.data?.payload ?? {};

		if (!type) {
			return;
		}
		let data = {
			type,
			requestId,
			name,
			payload
		};

		if (type === Channel.EventRequest) {
			this.onRequest(data);
		} else {
			this.onResponse(data);
		}
	}

	/**
	 * Wenn der type der eingehenden Nachricht ein Response ist,
	 * dann fängt diese Funktion diese Nachricht ab. Hierbei wird
	 * der hinterleget Promise resolved oder rejected.
	 *
	 * @param {Object} response Informationen vom Message.
	 */
	onResponse(response) {
		if (!this.sendetRequests[response.requestId]) {
			return;
		}

		if (response.type === Channel.EventResponseResolve) {
			this.sendetRequests[response.requestId].resolve(response.payload, this);
		} else if (response.type === Channel.EventResponseReject) {
			this.sendetRequests[response.requestId].reject(response.payload, this);
		}
		delete this.sendetRequests[response.requestId];
	}

	/**
	 * Wenn die eingegangene Nachricht vom Type Request ist, dann
	 * werden diese Nachricht von dieser Funktion abgefangen.
	 * Der Funktionsaufruf wird an den handler weitergegeben.
	 *
	 * @param {Object} request Informationen vom Message.
	 */
	onRequest(request) {
		if (!this.handler[request.name]) {
			if (!this.queuedRequests[request.name]) {
				this.queuedRequests[request.name] = [];
			}
			this.queuedRequests[request.name].push(request);
			return;
		}

		let bindingPromise = Promise.resolve(this.handler[request.name](request.payload, this));
		bindingPromise.then((value) => {
			this.port.postMessage({
				requestId: request.requestId,
				type: Channel.EventResponseResolve,
				name: request.name,
				payload: value
			});
		}).catch((reason) => {
			this.port.postMessage({
				requestId: request.requestId,
				type: Channel.EventResponseReject,
				name: request.name,
				payload: reason
			});
		});
	}
}
